<template>
  <ConfirmModal
    v-if="show"
    persist
    wrapper-class="min-w-[600px]"
    :approve="t('Meetings.myMeetings.cancelDialog.confirm')"
    :cancel="t('Meetings.myMeetings.cancelDialog.cancel')"
    data-testid="meetings-cancel-dialog"
    @close-dialog="handleClose"
  >
    <template #title>
      <h1 class="p-6 pb-0 text-headline-m">{{ t('Meetings.myMeetings.cancelDialog.title') }}</h1>
    </template>
    <template #content>
      <div class="space-y-4 px-6 pt-4">
        <p class="text-body-m text-subtle">{{ t('Meetings.myMeetings.cancelDialog.description') }}</p>
        <div>
          <label class="mb-1 block txt-body-s-medium text-subtle">
            {{ t('Meetings.myMeetings.cancelDialog.reasonLabel') }}
          </label>
          <SingleSelect
            v-model="selectedReason"
            :options="reasonOptions"
            :label="t('Meetings.myMeetings.cancelDialog.selectReason')"
            class="w-full"
            data-testid="meetings-cancel-reason-select"
          />
        </div>
        <TextInput
          v-model="customReason"
          type="textarea"
          :max-length="200"
          :label="t('Meetings.myMeetings.cancelDialog.customReasonLabel')"
          :placeholder="t('Meetings.myMeetings.cancelDialog.customReasonPlaceholder')"
          data-testid="meetings-cancel-custom-reason"
        />
      </div>
    </template>
  </ConfirmModal>
</template>

<script setup lang="ts">
import { TextInput } from '@ramp106/omrjs-core-ui/deprecated'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import ConfirmModal from '@/components/ConfirmModal.vue'
import SingleSelect from '@/ui/SingleSelect.vue'

const props = defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  (e: 'confirm', reason: string): void
  (e: 'cancel'): void
}>()

const { t } = useI18n()

const selectedReason = ref('')
const customReason = ref('')

const reasonOptions = computed(() => [
  { value: 'fully_booked', text: t('Meetings.myMeetings.cancelDialog.fullyBooked') },
  { value: 'scheduling_conflict', text: t('Meetings.myMeetings.cancelDialog.schedulingConflict') },
  { value: 'contact_unavailable', text: t('Meetings.myMeetings.cancelDialog.contactUnavailable') },
  { value: 'schedule_adjusted', text: t('Meetings.myMeetings.cancelDialog.scheduleAdjusted') },
  { value: 'other', text: t('Meetings.myMeetings.cancelDialog.other') },
])

const finalReason = computed(() => {
  if (customReason.value.trim()) {
    return customReason.value.trim()
  }
  if (selectedReason.value && selectedReason.value !== 'other') {
    const option = reasonOptions.value.find((opt) => opt.value === selectedReason.value)
    return option?.text ?? ''
  }
  return ''
})

watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      selectedReason.value = ''
      customReason.value = ''
    }
  },
)

function handleClose(confirmed: boolean) {
  if (confirmed) {
    emit('confirm', finalReason.value)
  } else {
    emit('cancel')
  }
}
</script>
